// Single restart control — the one operator-facing way to restart OneStone.
// Lives next to the active-project dropdown in the top nav (desktop) and the
// mobile header. REST-based (`POST /api/system/restart`) so it works identically
// on desktop loopback, the mobile PWA, and relay clients — one path, no IPC.
//
// Restart is disruptive (active agent turns are interrupted), so it is NEVER
// instant: a confirm dialog gates it, and a full-screen overlay covers the
// down-and-back window while a health poll reloads the UI once the fresh
// process answers.
import { useEffect, useRef, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@onestone/ui/button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { api, apiFetch } from '@/lib/api';
import { RESTART_INITIAL_DELAY_MS, RESTART_POLL_MS } from '@/lib/uiConstants';

export function RestartButton() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  async function handleRestart() {
    setError(null);
    try {
      await api('/api/system/restart', { method: 'POST' });
    } catch (err) {
      // A dropped connection right as the process goes down is EXPECTED — treat
      // only a clean non-2xx (still-up refusal, e.g. 501 non-Electron) as a real
      // failure. Network errors mean the restart already began.
      const msg = err instanceof Error ? err.message : String(err);
      if (/not available in this runtime/i.test(msg)) {
        setError(msg);
        throw err; // ConfirmDialog catches this and stays open to show the error
      }
    }
    // ConfirmDialog closes itself after onConfirm resolves.
    setRestarting(true);
    pollTimer.current = setTimeout(pollForRecovery, RESTART_INITIAL_DELAY_MS);
  }

  async function pollForRecovery() {
    try {
      const res = await apiFetch('/api/health');
      if (res.ok) {
        window.location.reload();
        return;
      }
    } catch {
      /* still down — keep waiting */
    }
    pollTimer.current = setTimeout(pollForRecovery, RESTART_POLL_MS);
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        aria-label="Restart OneStone"
        title="Restart OneStone"
        onClick={() => {
          setError(null);
          setConfirmOpen(true);
        }}
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Restart OneStone?"
        description="The app will close and reopen to apply any staged update and reload all configuration. Any in-progress agent turn will be interrupted. Your conversations are saved."
        confirmLabel="Restart"
        destructive
        onConfirm={handleRestart}
      >
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </ConfirmDialog>

      {restarting ? (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-3 bg-background/95 backdrop-blur-sm">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <p className="text-sm font-medium">Restarting OneStone…</p>
          <p className="text-xs text-muted-foreground">This reconnects automatically when it&apos;s back.</p>
        </div>
      ) : null}
    </>
  );
}
